import { browserApiClient, requiredApiData } from './browser-api';

export async function loadPushState(signal?: AbortSignal) {
  const response = await browserApiClient().GET('/api/v1/notifications/push', { signal });
  return requiredApiData(response.data, response.error, 'Notification settings are unavailable.');
}

export type PushState = Awaited<ReturnType<typeof loadPushState>>;
export type PushPreferences = Pick<
  PushState,
  'enabled' | 'social_thanks' | 'quiet_start_minute' | 'quiet_end_minute'
> & { timezone: string };

export async function savePushPreferences(body: PushPreferences) {
  const response = await browserApiClient({ mutation: true }).PUT(
    '/api/v1/notifications/push/preferences',
    { body },
  );
  return requiredApiData(
    response.data,
    response.error,
    'Notification preferences could not be saved.',
  );
}

export async function registerPushDevice(subscription: PushSubscription, label: string) {
  const data = subscription.toJSON();
  if (!data.endpoint || !data.keys?.p256dh || !data.keys.auth)
    throw new Error('The browser subscription is incomplete.');
  const response = await browserApiClient({ mutation: true }).POST(
    '/api/v1/notifications/push/devices',
    {
      body: {
        endpoint: data.endpoint,
        keys: { p256dh: data.keys.p256dh, auth: data.keys.auth },
        label,
      },
    },
  );
  return requiredApiData(response.data, response.error, 'This device could not be registered.');
}

export async function removePushDevice(id: string) {
  const response = await browserApiClient({ mutation: true }).DELETE(
    '/api/v1/notifications/push/devices/{device_id}',
    { params: { path: { device_id: id } } },
  );
  return requiredApiData(response.data, response.error, 'This device could not be removed.');
}

export async function selectPushDevice(id: string) {
  const response = await browserApiClient({ mutation: true }).POST(
    '/api/v1/notifications/push/devices/{device_id}/select',
    { params: { path: { device_id: id } } },
  );
  return requiredApiData(response.data, response.error, 'This device could not be selected.');
}

export async function testPushDevice() {
  const response = await browserApiClient({ mutation: true }).POST(
    '/api/v1/notifications/push/test',
    { body: {} },
  );
  return requiredApiData(
    response.data,
    response.error,
    'The test notification could not be requested.',
  );
}
